import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CellComponent, RowComponent, Tabulator } from 'tabulator-tables';
import useHealthCareManagement from '@/domains/asset-management/hooks/useHealthCareManagement';
import type {
  ButtonState,
  CreateHealthCareFacilityPayload,
  HealthCareFacilityDetailData,
  HealthCareFacilityFormValues,
  UpdateHealthCareFacilityPayload,
} from '@/domains/asset-management/types/healthcare';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import { ReactTabulator } from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
import TablePagination from '@/shared/components/ui/TablePagination/TablePagination';
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { remoteSortHandler, useServerTable } from '@/shared/hooks/useServerTable';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const commonColumnProps = {
  hozAlign: 'center' as const,
  headerHozAlign: 'center' as const,
  resizable: false as const,
};

const HEALTHCARE_SORT_FIELD_MAP: Record<string, string> = {
  health_care_facility_id: 'id',
  updated_time: 'updated_time',
};

const emptyFormValues: HealthCareFacilityFormValues = {
  health_care_facility_id: null,
  health_care_facility_type: null,
  ownership_type: null,
  name: null,
  is_emergency_24_h: null,
  is_open_24_h: null,
  health_care_pic_representative: null,
  health_care_pic_phone: null,
  latitude: null,
  longitude: null,
  ambulance_pic_name: null,
  ambulance_pic_phone: null,
  address: null,
};

const HealthCareManagement = () => {
  const { t } = useTranslation();
  const yesNoOptions = useMemo(
    () => [
      { value: 'true', label: t('cmn > option yes') },
      { value: 'false', label: t('cmn > option no') },
    ],
    [t]
  );

  const tbRef = useRef<Tabulator | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [disabledForm, setDisabledForm] = useState<boolean>(true);
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const [newId, setNewId] = useState<number | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<{
    health_care_facility_id: number | null;
  }>({ health_care_facility_id: null });
  const selectedFacilityRef = useRef(selectedFacility);
  const [typeFilter, setTypeFilter] = useState<string>('');

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, []);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    selectedFacilityRef.current = selectedFacility;
  }, [hasChangesUpdate, hasChangesCreate, selectedFacility]);

  const table = useServerTable<{ input?: string; health_care_facility_type?: string }>({
    defaultSort: { field: 'updated_time', dir: 'desc' },
    defaultSize: 10,
  });
  const queryParams = table.queryString;

  const { commonListData } = useCommonCodes({ optionParams: 'upper-code=HLTH_FAC_TP' });
  const facilityTypeOptions = useMemo(
    () =>
      commonListData?.HLTH_FAC_TP?.code?.map((code, index) => ({
        value: code ?? '',
        label: commonListData.HLTH_FAC_TP?.name[index] ?? '',
      })) ?? [],
    [commonListData]
  );

  const { commonListData: ownershipCommonListData } = useCommonCodes({
    optionParams: 'upper-code=HLTH_OWN_TP',
  });
  const ownershipTypeOptions = useMemo(
    () =>
      ownershipCommonListData?.HLTH_OWN_TP?.code?.map((code, index) => ({
        value: code ?? '',
        label: ownershipCommonListData.HLTH_OWN_TP?.name[index] ?? '',
      })) ?? [],
    [ownershipCommonListData]
  );

  const [formValues, setFormValues] = useState<HealthCareFacilityFormValues>(emptyFormValues);
  const emptyDetail = useCallback(() => setFormValues(emptyFormValues), []);

  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = useCallback(() => {
    setButtonState({ confirm: true, cancel: true, delete: true, create: true });
  }, []);

  const enableInitialButtons = useCallback(() => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prev) => ({ ...prev, create: false }));
  }, [disableAllButtons]);

  const enableUPDATEButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false, delete: false, create: false }));
  }, [disableAllButtons]);

  const enableRegisterButtons = useCallback(() => {
    disableAllButtons();
    setButtonState((prev) => ({ ...prev, cancel: false }));
  }, [disableAllButtons]);

  const reloadCallback = useCallback(() => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabledForm(true);
    setHasChangesUpdate(false);
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setSelectedFacility({ health_care_facility_id: null });
    setNewId(null);
  }, [enableInitialButtons, emptyDetail]);

  const {
    healthCareFacilityListData,
    detailHealthCareFacilityData,
    deleteHealthCareFacility,
    createHealthCareFacility,
    updateHealthCareFacility,
    refetchDetailHealthCareFacility,
  } = useHealthCareManagement({
    healthCareFacilityId: selectedFacility?.health_care_facility_id,
    queryParams,
    onDeleteSuccess: reloadCallback,
    onUpdateSuccess: () => {
      refetchDetailHealthCareFacility();
      enableUPDATEButtons();
      setIsNewClicked(false);
      setHasChangesUpdate(false);
    },
    onCreateSuccess: (responseData) => {
      reloadCallback();
      setNewId(responseData.health_care_facility_id);
    },
  });

  const pageData = healthCareFacilityListData?.data;
  const rows = pageData?.rows ?? [];
  const dataFacility = detailHealthCareFacilityData?.data;

  const healthcareColumns = useMemo(
    () => [
      {
        title: t('cmn > no'),
        formatter: (cell: CellComponent) =>
          (table.page - 1) * table.size + cell.getRow().getPosition(true),
        width: 60,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > healthcare id'),
        field: 'health_care_facility_id',
        widthGrow: 1,
        headerSort: true,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > healthcare name'),
        field: 'name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > healthcare type'),
        field: 'health_care_facility_type_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > ownership type'),
        field: 'ownership_type_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > healthcare pic phone number'),
        field: 'health_care_pic_phone',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > ambulance pic name'),
        field: 'ambulance_pic_name',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > ambulance pic phone'),
        field: 'ambulance_pic_phone',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
      {
        title: t('healthcare > address'),
        field: 'address',
        widthGrow: 1,
        headerSort: false,
        ...commonColumnProps,
      },
    ],
    [table.page, table.size, t]
  );

  const detailToFormValues = useCallback(
    (data: HealthCareFacilityDetailData): HealthCareFacilityFormValues => ({
      health_care_facility_id: data.health_care_facility_id,
      health_care_facility_type: data.health_care_facility_type,
      ownership_type: data.ownership_type,
      name: data.name,
      is_emergency_24_h: data.is_emergency_24_h === null ? null : String(data.is_emergency_24_h),
      is_open_24_h: data.is_open_24_h === null ? null : String(data.is_open_24_h),
      health_care_pic_representative: data.health_care_pic_representative,
      health_care_pic_phone: data.health_care_pic_phone,
      latitude: data.latitude,
      longitude: data.longitude,
      ambulance_pic_name: data.ambulance_pic_name,
      ambulance_pic_phone: data.ambulance_pic_phone,
      address: data.address,
    }),
    []
  );

  useEffect(() => {
    if (dataFacility) setFormValues(detailToFormValues(dataFacility));
  }, [dataFacility, detailToFormValues]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormValues((prev) => ({ ...prev, [name]: value }));

      if (isNewClicked) {
        setHasChangesCreate(true);
      } else if (selectedFacilityRef.current?.health_care_facility_id) {
        setHasChangesUpdate(true);
      }
    },
    [isNewClicked]
  );

  const languageTabulator = () => ({
    pagination: {
      first: t('cmn > first page'),
      first_title: t('cmn > first page'),
      last: t('cmn > last page'),
      last_title: t('cmn > last page'),
      prev: t('cmn > page before'),
      prev_title: t('cmn > page before'),
      next: t('cmn > next page'),
      next_title: t('cmn > next page'),
    },
  });

  const optionsTabulator = {
    debugInvalidOptions: true,
    // Server-side pagination: Tabulator's own pager is off; <TablePagination> drives it.
    pagination: false,
    rowHeight: 41,
    selectableRows: false,
    movableRows: false,
    resizableRows: false,
    index: 'health_care_facility_id',
    locale: 'ko',
    langs: { ko: languageTabulator() },
    selectableRowsCheck: (row: RowComponent) =>
      !row.getElement().classList.contains('tabulator-selected'),
    placeholder: `<div style='text-align:center;'>${t('msg > no data')}</div>`,
  };

  const handleSearch = useCallback(
    (inputVal: string) => {
      table.setFilters({ input: inputVal || undefined });
    },
    [table]
  );

  const handleReset = useCallback(() => {
    setTypeFilter('');
    table.reset();
  }, [table]);

  const handleTypeFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setTypeFilter(e.target.value);
      table.setFilters({ health_care_facility_type: e.target.value || undefined });
    },
    [table]
  );

  const handleRowSelected = useCallback(
    (row: RowComponent) => {
      const selectRow = () => {
        tbRef.current?.deselectRow();
        row.select();
        row.getElement().classList.add('custom-hover');

        const rowData = row.getData();
        setSelectedFacility({ health_care_facility_id: rowData.health_care_facility_id });
        setDisabledForm(false);
        setHasChangesUpdate(false);
        setHasChangesCreate(false);
        setIsNewClicked(false);
        enableUPDATEButtons();

        requestAnimationFrame(() => nameRef.current?.focus());
      };

      if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(selectRow);
      } else {
        selectRow();
      }
    },
    [enableUPDATEButtons, t]
  );

  const handleNewButtonClick = useCallback(() => {
    const startNew = () => {
      tbRef.current?.deselectRow();
      emptyDetail();
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedFacility({ health_care_facility_id: null });
      setDisabledForm(false);
      setHasChangesUpdate(false);
      requestAnimationFrame(() => nameRef.current?.focus());
    };

    if (hasChangesCreate || hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(startNew);
    } else {
      startNew();
    }
  }, [hasChangesCreate, hasChangesUpdate, enableRegisterButtons, t, emptyDetail]);

  const handleCancelButtonClick = useCallback(() => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        setHasChangesUpdate(false);
        setSelectedFacility({ health_care_facility_id: null });
        enableInitialButtons();
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), { mode: 'confirm' });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledForm(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabledForm(true);
        enableInitialButtons();
        setHasChangesCreate(false);
      }
    } else {
      reloadCallback();
    }
  }, [
    hasChangesUpdate,
    hasChangesCreate,
    isNewClicked,
    enableInitialButtons,
    reloadCallback,
    t,
    emptyDetail,
  ]);

  const buildPayload = useCallback((): CreateHealthCareFacilityPayload | null => {
    const { name, health_care_facility_type, ownership_type } = formValues;
    if (!name?.trim() || !health_care_facility_type || !ownership_type) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > required values'));
      return null;
    }
    return {
      health_care_facility_type,
      ownership_type,
      name: name.trim(),
      is_emergency_24_h:
        formValues.is_emergency_24_h === null ? null : formValues.is_emergency_24_h === 'true',
      is_open_24_h: formValues.is_open_24_h === null ? null : formValues.is_open_24_h === 'true',
      health_care_pic_representative: formValues.health_care_pic_representative || null,
      health_care_pic_phone: formValues.health_care_pic_phone || null,
      latitude:
        formValues.latitude === '' || formValues.latitude === null
          ? null
          : Number(formValues.latitude),
      longitude:
        formValues.longitude === '' || formValues.longitude === null
          ? null
          : Number(formValues.longitude),
      ambulance_pic_name: formValues.ambulance_pic_name || null,
      ambulance_pic_phone: formValues.ambulance_pic_phone || null,
      address: formValues.address || null,
    };
  }, [formValues, t]);

  const handleRegistButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload) return;
    createHealthCareFacility(payload);
  }, [createHealthCareFacility, buildPayload]);

  const handleConfirmButtonClick = useCallback(() => {
    const payload = buildPayload();
    if (!payload || !formValues.health_care_facility_id) return;
    const updatePayload: UpdateHealthCareFacilityPayload = {
      ...payload,
      health_care_facility_id: Number(formValues.health_care_facility_id),
    };
    updateHealthCareFacility({
      healthCareFacilityData: updatePayload,
      params: { health_care_facility_id: updatePayload.health_care_facility_id },
    });
  }, [formValues, updateHealthCareFacility, buildPayload]);

  const handleDeleteButtonClick = useCallback(() => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), { mode: 'confirm' });
    message.confirmClicked().then(() => {
      if (selectedFacility?.health_care_facility_id)
        deleteHealthCareFacility(selectedFacility.health_care_facility_id);
    });
  }, [selectedFacility, deleteHealthCareFacility, t]);

  const logData = dataFacility
    ? [
        { label: t('cmn > registered by'), value: dataFacility.registered_by },
        {
          label: t('cmn > registered time'),
          value: dataFacility.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataFacility.registered_time))
            : dataFacility.registered_time,
        },
        { label: t('cmn > updated by'), value: dataFacility.updated_by },
        {
          label: t('cmn > updated time'),
          value: dataFacility.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(dataFacility.updated_time))
            : dataFacility.updated_time,
        },
      ]
    : [];

  return (
    <section className="wrap">
      <div className="header-title">
        <h3>{t('ASSET')}</h3>
        <h3>&gt;</h3>
        <h3>{t('ASSET-HEALTHCARE')}</h3>
      </div>

      <ContainerCard>
        <Filtering
          searchRef={searchRef}
          labelSelect={t('cmn > filter by type')}
          placeholder={t('healthcare > search by id or name')}
          disableFiltering={true}
          onReset={handleReset}
          onSearch={handleSearch}
        >
          <Select
            value={typeFilter}
            onChange={handleTypeFilterChange}
            options={[{ value: '', label: t('cmn > all') }, ...facilityTypeOptions]}
          />
        </Filtering>
      </ContainerCard>

      <ContainerCard>
        <div className="table-container relative">
          <ReactTabulator
            data={rows}
            columns={healthcareColumns}
            layout="fitDataStretch"
            options={optionsTabulator}
            className="tabulator-custom"
            onRef={(r) => {
              tbRef.current = r.current;
            }}
            events={{
              rowClick: (_e: UIEvent, row: RowComponent) => handleRowSelected(row),
              dataSorted: remoteSortHandler(table, HEALTHCARE_SORT_FIELD_MAP),
              tableBuilt: () => {
                if (!tbRef.current || !newId) return;
                try {
                  const row = tbRef.current.getRow(newId) || null;
                  if (row && typeof row.select === 'function') {
                    tbRef.current.scrollToRow(row, 'bottom', true);
                    handleRowSelected(row);
                  }
                } catch {
                  /* row not found */
                }
              },
            }}
          />
        </div>
        <TablePagination
          total={pageData?.total ?? 0}
          page={table.page}
          size={table.size}
          totalPages={pageData?.total_pages ?? 0}
          onPageChange={table.setPage}
          centered
        />
      </ContainerCard>

      <ContainerCard className="flex flex-col">
        <div className="grid grid-cols-2  h-full items-start gap-6">
          <div className="grid grid-cols-1 gap-3 h-full">
            <DetailForm
              className="items-center!"
              label={t('cmn > id')}
              disabled={true}
              value={formValues.health_care_facility_id ?? ''}
              name="health_care_facility_id"
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > healthcare name')}
              formRef={nameRef}
              required={true}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.name || ''}
              name="name"
              maxLength={100}
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > healthcare type')}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.health_care_facility_type || ''}
              name="health_care_facility_type"
              optionSelect={facilityTypeOptions}
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > ownership type')}
              required={true}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.ownership_type || ''}
              name="ownership_type"
              optionSelect={ownershipTypeOptions}
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > open 24 hours')}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.is_open_24_h || ''}
              name="is_open_24_h"
              optionSelect={yesNoOptions}
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > emergency 24 hours')}
              disabled={disabledForm}
              inputType="select"
              onChange={handleInputChange}
              value={formValues.is_emergency_24_h || ''}
              name="is_emergency_24_h"
              optionSelect={yesNoOptions}
            />
          </div>
          <div className="grid grid-cols-1 grid-rows-[auto_auto_auto_auto_1fr] gap-3 h-full items-start">
            <DetailForm
              className="items-center!"
              label={t('healthcare > healthcare pic name')}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.health_care_pic_representative || ''}
              name="health_care_pic_representative"
              maxLength={100}
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > healthcare pic phone number')}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.health_care_pic_phone || ''}
              name="health_care_pic_phone"
              maxLength={30}
              pattern={'^[0-9]*$'}
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > ambulance pic name')}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.ambulance_pic_name || ''}
              name="ambulance_pic_name"
              maxLength={100}
            />
            <DetailForm
              className="items-center!"
              label={t('healthcare > ambulance pic phone number')}
              disabled={disabledForm}
              onChange={handleInputChange}
              value={formValues.ambulance_pic_phone || ''}
              name="ambulance_pic_phone"
              maxLength={30}
              pattern={'^[0-9]*$'}
            />
            <DetailForm
              className="items-center!"
              label={t('cmn > latitude longitude')}
              inputType="text"
              showInput={false}
            >
              <div className="flex w-full flex-row gap-x-2">
                <GeneralInput
                  customInput="w-full"
                  disabled={disabledForm}
                  onChange={handleInputChange}
                  value={formValues.latitude ?? ''}
                  name="latitude"
                  maxLength={100}
                  pattern={'^-?[0-9.]*$'}
                />
                <GeneralInput
                  customInput="w-full"
                  disabled={disabledForm}
                  onChange={handleInputChange}
                  value={formValues.longitude ?? ''}
                  name="longitude"
                  maxLength={100}
                  pattern={'^-?[0-9.]*$'}
                />
              </div>
            </DetailForm>
          </div>
        </div>

        <div className="grid grid-cols-1">
          <DetailForm
            label={t('healthcare > address')}
            inputType="textarea"
            disabled={disabledForm}
            onChange={handleInputChange}
            value={formValues.address || ''}
            name="address"
            maxLength={500}
            className="h-full [&>div:has(textarea)]:h-full [&>div:has(textarea)]:w-full [&_textarea]:h-full [&_textarea]:w-full"
          />
        </div>

        <hr className="border-t border-gray-300" />
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex-1">{disabledForm ? null : <LogList logs={logData} />}</div>
          <div className="flex-none">
            <ButtonGroup
              onClickDelete={handleDeleteButtonClick}
              onClickNew={handleNewButtonClick}
              onClickCancel={handleCancelButtonClick}
              onClickRegist={handleRegistButtonClick}
              onClickConfirm={handleConfirmButtonClick}
              isNewClicked={isNewClicked}
              cancelButtonState={buttonState.cancel}
              confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
              deleteButtonState={buttonState.delete}
              newButtonState={hasChangesCreate ? false : buttonState.create}
            />
          </div>
        </div>
      </ContainerCard>
    </section>
  );
};

export default HealthCareManagement;
